import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { BACKDROP_CREDIT } from './Backdrop';

// The site's interactive coverage map, restyled to sit on the landing page's
// smoke backdrop (glass card, translucent land, cyan office markers).

interface Office {
  country: string;
  city: string;
  type: 'HQ' | 'Office' | 'Opening';
  coordinates: { lng: number; lat: number };
  // where the country label sits relative to the dot (map units), chosen so neighbours do not overlap
  label: { dx: number; dy: number; anchor: 'start' | 'end' };
}

const MAP_GEO_JSON = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const NOT_SERVED = new Set(['Antarctica']);
const LAND = 'rgba(255,255,255,0.14)';
const LAND_STROKE = 'rgba(255,255,255,0.28)';
const LAND_HOVER = '#0E9ED5';
const NOT_SERVED_FILL = 'rgba(255,255,255,0.04)';
const OPENING_COLOR = '#F97316';

const stats = [
  { value: '140+', label: 'Countries served' },
  { value: '6', label: 'TrueLog offices' },
  { value: '1', label: 'Accountable partner' },
];

const offices: Office[] = [
  { country: 'Singapore', city: 'Singapore', type: 'HQ', coordinates: { lng: 103.8198, lat: 1.3521 }, label: { dx: 14, dy: 5, anchor: 'start' } },
  { country: 'Belgium', city: 'Antwerp', type: 'Office', coordinates: { lng: 4.4025, lat: 51.2194 }, label: { dx: -12, dy: 5, anchor: 'end' } },
  { country: 'UAE', city: 'Dubai', type: 'Office', coordinates: { lng: 55.2708, lat: 25.2048 }, label: { dx: -12, dy: 5, anchor: 'end' } },
  { country: 'Vietnam', city: 'Ho Chi Minh City', type: 'Office', coordinates: { lng: 106.6297, lat: 10.8231 }, label: { dx: 12, dy: -4, anchor: 'start' } },
  { country: 'India', city: 'Chennai', type: 'Office', coordinates: { lng: 80.2707, lat: 13.0827 }, label: { dx: -12, dy: -3, anchor: 'end' } },
  { country: 'Sri Lanka', city: 'Colombo', type: 'Office', coordinates: { lng: 79.8612, lat: 6.9271 }, label: { dx: -12, dy: 12, anchor: 'end' } },
  // planned office, shown in orange with the "2027 opening" legend entry
  { country: 'USA', city: 'Miami', type: 'Opening', coordinates: { lng: -80.1918, lat: 25.7617 }, label: { dx: -12, dy: 5, anchor: 'end' } },
];

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const LandingMap: React.FC = () => {
  const [hoveredOffice, setHoveredOffice] = useState<Office | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  const isSame = (a: Office, b: Office | null) => Boolean(b && a.city === b.city && a.country === b.country);

  return (
    <section className="relative py-16 text-white lg:py-24">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-2xl">
            <p className="lp-eyebrow">Global coverage</p>
            <h2 className="lp-h2 mt-4">
              Our global footprint keeps
              <br />
              <span className="text-white/45">ICT moving, everywhere</span>
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-7 text-white/65">
              We service 140+ countries across every continent through one accountable network, anchored by our own
              offices in six locations. Hover the map to explore.
            </p>
          </div>
          <Link to="/global-coverage" className="lp-btn-primary self-start lg:self-auto">
            Explore our coverage
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </motion.div>

        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className="lp-card px-3 py-5 text-center sm:px-5"
            >
              <p className="lp-display text-[1.7rem] text-white sm:text-[2.2rem]">{stat.value}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="lp-card mt-4 p-4 sm:p-6"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="lp-h3">Interactive world map</h3>
            <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-white/55">
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-3.5 rounded-sm border" style={{ backgroundColor: LAND, borderColor: LAND_STROKE }} />
                Countries we serve
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#0E9ED5] shadow-[0_0_8px_#0E9ED5]" />
                Office
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-white shadow-[0_0_8px_#fff]" />
                HQ
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#F97316] shadow-[0_0_8px_#F97316]" />
                2027 opening
              </span>
            </div>
          </div>

          <div className="relative h-[420px] overflow-hidden rounded-2xl border border-white/10 bg-[#04060d]/55 md:h-[540px]">
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:48px_48px]" />
            <ComposableMap
              projectionConfig={{ scale: 170, center: [8, 12] }}
              className="h-full w-full"
              style={{ width: '100%', height: '100%' }}
            >
              <Geographies geography={MAP_GEO_JSON}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const name: string = geo.properties?.name || '';
                    const served = !NOT_SERVED.has(name);
                    const fill = !served ? NOT_SERVED_FILL : hoveredCountry === name ? LAND_HOVER : LAND;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke={served ? LAND_STROKE : 'rgba(255,255,255,0.08)'}
                        strokeWidth={0.45}
                        onMouseEnter={() => served && setHoveredCountry(name)}
                        onMouseLeave={() => setHoveredCountry(null)}
                        style={{
                          default: { fill, outline: 'none', transition: 'fill 0.25s ease' },
                          hover: { fill: served ? LAND_HOVER : NOT_SERVED_FILL, outline: 'none' },
                          pressed: { fill, outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {offices.map((office, index) => {
                const active = isSame(office, hoveredOffice);
                const isHq = office.type === 'HQ';
                const color = isHq ? '#ffffff' : office.type === 'Opening' ? OPENING_COLOR : '#0E9ED5';
                return (
                  <Marker
                    key={`${office.city}-${office.country}`}
                    coordinates={[office.coordinates.lng, office.coordinates.lat]}
                    onMouseEnter={() => setHoveredOffice(office)}
                    onMouseLeave={() => setHoveredOffice(null)}
                  >
                    <motion.g
                      className="cursor-pointer"
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: index * 0.05 }}
                      whileHover={{ scale: 1.18 }}
                    >
                      <circle r={isHq ? 12 : 9} fill={color} opacity={0.18} />
                      <circle r={isHq ? 6 : 4.5} fill={color} stroke="rgba(255,255,255,0.85)" strokeWidth={isHq ? 1.8 : 1.4} />
                      {(isHq || active) && (
                        <circle r={isHq ? 14 : 11} fill="none" stroke={color} strokeWidth={1.2} opacity={0.7} className="animate-ping" />
                      )}
                      <text
                        x={office.label.dx}
                        y={office.label.dy}
                        textAnchor={office.label.anchor}
                        fontSize={14}
                        fontWeight={600}
                        fill={isHq ? '#ffffff' : 'rgba(255,255,255,0.85)'}
                        stroke="rgba(4,6,13,0.9)"
                        strokeWidth={3}
                        paintOrder="stroke"
                        style={{ letterSpacing: '0.04em', pointerEvents: 'none' }}
                      >
                        {office.country}
                      </text>
                    </motion.g>
                  </Marker>
                );
              })}
            </ComposableMap>

            <AnimatePresence>
              {(hoveredOffice || hoveredCountry) && (
                <motion.div
                  key={hoveredOffice ? `o-${hoveredOffice.city}` : `c-${hoveredCountry}`}
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.96 }}
                  className="pointer-events-none absolute left-4 top-4 z-20 rounded-xl border border-white/15 bg-[#070b16]/85 px-4 py-3 backdrop-blur-lg"
                >
                  {hoveredOffice ? (
                    <>
                      <p className="text-sm font-semibold text-white">
                        {hoveredOffice.city}, {hoveredOffice.country}
                      </p>
                      <p className="text-xs text-white/55">
                        {hoveredOffice.type === 'Opening' ? 'TrueLog Office · Opening 2027' : `TrueLog ${hoveredOffice.type} · Operational`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-white">{hoveredCountry}</p>
                      <p className="text-xs text-white/55">Serviced by the TrueLog network</p>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {BACKDROP_CREDIT && (
          <p className="mt-4 text-right text-[10px] uppercase tracking-[0.18em] text-white/35">
            <a href={BACKDROP_CREDIT.href} target="_blank" rel="noopener noreferrer" className="hover:text-white/70">
              {BACKDROP_CREDIT.text}
            </a>
          </p>
        )}
      </div>
    </section>
  );
};

export default LandingMap;
